import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./select-menu-header.types";
import { ActionIcon, Group, Text } from "@mantine/core";

import { faChevronDoubleRight } from "@fortawesome/pro-solid-svg-icons/faChevronDoubleRight";
import { faChevronDoubleLeft } from "@fortawesome/pro-solid-svg-icons/faChevronDoubleLeft";
import { Icon } from "../../../icon";
import { Badge } from "../../../badge";
import { Tooltip } from "../../../tooltip";

/**
 * Header of a multiple select menu, with the count of each list and the buttons to select or deselect them all.
 * @param props.notSelectedItems               Items not selected.
 * @param props.hideBulkActionSelectAll        Whether to hide the "select all" button.
 * @param props.selectAllBulkActionTooltipText Tooltip shown when the "select all" button is disabled, if any.
 * @param props.isSelectAllDisabled            Whether the "select all" button is disabled.
 * @param props.onSelectAll                    Select all of the items not selected.
 * @param props.hideBulkActionDeselectAll      Whether to hide the "deselect all" button.
 * @param props.deselectAllDisabledTooltip     Tooltip shown when the "deselect all" button is disabled, if any.
 * @param props.isDeselectAllDisabled          Whether the "deselect all" button is disabled.
 * @param props.onSelectNone                   Deselect the selected items.
 * @param props.itemLimit                      Optional maximum number of items that can be selected.
 * @param props.selectedItems                  Items selected.
 * @param props.selectedSearchMatchesCount     How many selected items match the search, when there is one.
 * @returns                                    Header of the select menu.
 */
const SelectMenuHeader: FunctionComponent<IProps> = ({
    notSelectedItems,
    hideBulkActionSelectAll,
    selectAllBulkActionTooltipText,
    isSelectAllDisabled,
    onSelectAll,
    hideBulkActionDeselectAll,
    deselectAllDisabledTooltip,
    isDeselectAllDisabled,
    onSelectNone,
    itemLimit,
    selectedItems,
    selectedSearchMatchesCount,
}) => {
    // While searching, the selected list only shows the items that match, so the count says how many of them that is.
    // With nothing selected there is nothing to count out of.
    const selectedCount = useMemo(
        () =>
            typeof selectedSearchMatchesCount === "number" && selectedItems.length > 0
                ? `${selectedSearchMatchesCount} of ${selectedItems.length}`
                : selectedItems.length,
        [selectedSearchMatchesCount, selectedItems.length]
    );

    return (
        <Group justify="space-between" gap="sm" align="center" p="md">
            <Group flex={1} align="center" justify="space-between">
                <Text size="md">Not selected ({notSelectedItems.length})</Text>
                <Group align="center" justify="space-between">
                    {!hideBulkActionSelectAll && (
                        <Tooltip
                            label={selectAllBulkActionTooltipText}
                            disabled={!isSelectAllDisabled || !selectAllBulkActionTooltipText}
                        >
                            <ActionIcon
                                variant="default"
                                size="lg"
                                color="gray.6" // TODO: Watchtower 2.0 - This would be replaced with the renamed token `GRAY` created by the designers.
                                disabled={isSelectAllDisabled}
                                onClick={onSelectAll}
                                aria-label="Select all"
                                data-testid="add-all-button"
                            >
                                <Icon size="xs" icon={faChevronDoubleRight} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            </Group>
            <Group flex={1} align="center" justify="space-between">
                <Group align="center" justify="space-between">
                    {!hideBulkActionDeselectAll && (
                        <Tooltip
                            label={deselectAllDisabledTooltip}
                            disabled={!isDeselectAllDisabled || !deselectAllDisabledTooltip}
                        >
                            <ActionIcon
                                variant="default"
                                size="lg"
                                color="gray.6" // TODO: Watchtower 2.0 - This would be replaced with the renamed token `GRAY` created by the designers.
                                disabled={isDeselectAllDisabled}
                                onClick={onSelectNone}
                                aria-label="Select none"
                                data-testid="remove-all-button"
                            >
                                <Icon size="xs" icon={faChevronDoubleLeft} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
                <Group align="center" gap="sm" justify="space-between">
                    {itemLimit && (
                        <Badge
                            color={
                                itemLimit === selectedItems.length
                                    ? "var(--mantine-color-error-light-hover)"
                                    : "var(--mantine-color-warning-light-hover)"
                            }
                        >
                            <Text
                                c={
                                    itemLimit === selectedItems.length
                                        ? "var(--mantine-color-text-error-fade-1)"
                                        : "var(--mantine-color-text-warning-fade-1)"
                                }
                                span
                                size="xs"
                            >
                                Max {itemLimit}
                            </Text>
                        </Badge>
                    )}
                    <Text size="md">Selected ({selectedCount})</Text>
                </Group>
            </Group>
        </Group>
    );
};

export default memo(SelectMenuHeader);
